import React, { FC, memo, useMemo } from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import { isArray } from 'lodash-es';
import { preprocessMarkdown } from '@/pages/aigc/pages/Agent/ChatPage/utils';
import SingleQA from '@/pages/aigc/pages/Agent/ChatPage/components/SingleQA';
import Abnormal from '@home/components/Abnormal';
import SkillDetailEmptyState from '../SkillDetailEmptyState';
import useSkillLinkageSessionData from '../../hooks/useSkillLinkageSessionData';
import type { ISkillLinkage } from '../../../types/skillDetail';
import './index.scss';

export interface ISkillLinkageSessionPreviewProps {
  /** 当前联动案例 */
  linkage: ISkillLinkage;
  /** 是否英文环境 */
  isEN: boolean;
  /** 是否启用数据请求 */
  enabled?: boolean;
}

const PREFIX = 'dba-skill-linkage-session-preview';

const SkillLinkageSessionPreview: FC<ISkillLinkageSessionPreviewProps> = ({
  linkage,
  isEN,
  enabled = true,
}) => {
  const { formatMessage } = useIntl();
  const { data, loading } = useSkillLinkageSessionData(linkage, isEN, enabled);

  const snapshotRecord = useMemo(() => {
    if (!data || !isArray(data.messages)) {
      return [];
    }
    // 对回答内容做 markdown 预处理，与分享页展示保持一致
    const processItem = (answer: any) => {
      const { type, value } = answer || {};
      if (type === 'think') {
        return {
          ...answer,
          value: value?.map((task: any) => {
            if (task.type === 'think') {
              return { ...task, value: preprocessMarkdown(task?.value) };
            }
            return task;
          }),
        };
      }
      if (type !== 'text') {
        return answer;
      }
      return { ...answer, value: preprocessMarkdown(value) };
    };
    return (data.messages || []).map((conversation: any) => ({
      ...conversation,
      answer: (conversation.answer || []).map(processItem),
    }));
  }, [data]);

  const username = data?.user_id;

  if (loading) {
    return (
      <div className={PREFIX}>
        <Abnormal
          isLoading
          style={{
            height: '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        />
      </div>
    );
  }

  if (!data || snapshotRecord.length === 0) {
    return (
      <div className={classNames(PREFIX, `${PREFIX}--empty`)}>
        <SkillDetailEmptyState
          message={formatMessage({ id: 'dba_skill_detail_linkage_empty' })}
        />
      </div>
    );
  }

  return (
    <section className={classNames('dba-chat-page', PREFIX)} data-is-snapshot={true}>
      <div className="dba-chat-scroll">
        <div className="dba-chat-content">
          {snapshotRecord.map((conversation: any) => {
            const fileData = conversation?.fileData;
            const fileListResult =
              fileData?.length > 0 ? fileData : conversation?.frontFileList;
            const fileListData = { [conversation?.messageId]: fileListResult };
            return (
              <SingleQA
                {...{ ...conversation, conversations: snapshotRecord }}
                key={conversation.messageId}
                question={conversation?.query}
                streamingState={null}
                isLastQA={null}
                isProcessing={false}
                userInfo={{ username }}
                editObj={{}}
                fileList={fileListData}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default memo(SkillLinkageSessionPreview);
